import { RouterProvider, createBrowserRouter, Navigate } from 'react-router-dom';
import React, { Suspense } from 'react';
import AppBody from './AppBody';
import ErrorBoundary from './components/ErrorBoundary';
import { ProgressCircle } from '@adobe/react-spectrum';
import { SearedLoader } from './components/genericLoadingDialog/SearedLoader';
import { RouteSuspense } from './components/common/RouteSuspense';

// Resolves a page's named export as the default React.lazy expects.
const lazyNamed = (load, name) => React.lazy(() => load().then((m) => ({ default: m[name] })));

const LeaderBoard = lazyNamed(() => import('./components/leaderboard/LeaderBoard'), 'LeaderBoard');
const Deck = lazyNamed(() => import('./components/deck/Deck'), 'Deck');
const Commanders = lazyNamed(() => import('./components/commanders/Commanders'), 'Commanders');
const CommanderDetails = lazyNamed(() => import('./components/commanders/CommanderDetails'), 'CommanderDetails');
const Archetypes = lazyNamed(() => import('./components/archetypes/Archetypes'), 'Archetypes');
const Users = lazyNamed(() => import('./components/users/Users'), 'Users');
const AuthorDetails = lazyNamed(() => import('./components/authors/AuthorDetails'), 'AuthorDetails');
const Bot = lazyNamed(() => import('./components/bot/Bot'), 'Bot');
const FAQ = lazyNamed(() => import('./components/faq/FAQ'), 'FAQ');
const Privacy = lazyNamed(() => import('./components/privacy/Privacy'), 'Privacy');
const Changelog = lazyNamed(() => import('./components/changelog/Changelog'), 'Changelog');
const Meta = lazyNamed(() => import('./components/meta/Meta'), 'Meta');
const Algorithm = lazyNamed(() => import('./components/algorithm/Algorithm'), 'Algorithm');
const PowerLevels = lazyNamed(() => import('./components/powerlevels/PowerLevels'), 'PowerLevels');
const VerifyEmail = lazyNamed(() => import('./components/auth/VerifyEmail'), 'VerifyEmail');
const UserDetails = lazyNamed(() => import('./components/user/UserDetails'), 'UserDetails');
const LinkedAccountsContainer = React.lazy(() => import('./components/linkedAccounts/LinkedAccountsContainer'));
const ManageDecksContainer = React.lazy(() => import('./components/manageDecks/ManageDecksContainer'));
const SettingsContainer = React.lazy(() => import('./components/settings/SettingsContainer'));
const PlaytestPage = React.lazy(() => import('./components/playtest/PlaytestPage'));
const NotFound = lazyNamed(() => import('./components/notFound/NotFound'), 'NotFound');

// A page route: its own error boundary (which also catches a failed chunk load)
// around the lazily loaded page.
const page = (route, Page) => (
    <ErrorBoundary route={route}>
        <RouteSuspense>
            <Page />
        </RouteSuspense>
    </ErrorBoundary>
);

const router = createBrowserRouter([
    {
        element: (
            <ErrorBoundary route={'*'}>
                <AppBody />
            </ErrorBoundary>
        ),
        children: [
            {
                path: '/',
                element: page('/', LeaderBoard),
            },
            {
                path: 'details/deck/:id/playtest',
                element: (
                    <ErrorBoundary route={'playtest'}>
                        <Suspense fallback={<SearedLoader size="large" subcaption="Warming up the playtest engine…" />}>
                            <PlaytestPage />
                        </Suspense>
                    </ErrorBoundary>
                ),
            },
            {
                path: '/details/*',
                element: page('details/*', Deck),
            },
            {
                path: 'commanders',
                element: page('commanders', Commanders),
            },
            {
                path: 'commanders/id/*',
                element: page('commanders/id/*', CommanderDetails),
            },
            {
                path: 'archetypes',
                element: page('archetypes', Archetypes),
            },
            {
                path: 'users',
                element: page('users', Users),
            },
            {
                // Browse-by-author was replaced with browse-by-users.
                // Preserve old bookmarks by redirecting /authors → /users.
                path: 'authors',
                element: <Navigate to="/users" replace />,
            },
            {
                // Author-detail pages remain — still linked from deck
                // pages, just no longer reachable from a list.
                path: 'authors/id/*',
                element: page('authors/id/*', AuthorDetails),
            },
            {
                path: 'bot',
                element: page('bot', Bot),
            },
            {
                path: 'faq',
                element: page('faq', FAQ),
            },
            // Privacy Policy — always reachable, in every env. Google
            // requires a live privacy policy to approve ads, and there's no
            // reason to ever hide it, so this route is intentionally ungated.
            {
                path: 'privacy',
                element: page('privacy', Privacy),
            },
            {
                path: 'changelog',
                element: page('changelog', Changelog),
            },
            {
                path: 'meta',
                element: page('meta', Meta),
            },
            {
                path: 'algorithm',
                element: page('algorithm', Algorithm),
            },
            {
                path: 'powerlevels',
                element: page('powerlevels', PowerLevels),
            },
            {
                path: 'linkedAccounts',
                element: page('linkedaccounts', LinkedAccountsContainer),
            },
            {
                path: 'managedecks',
                element: page('managedecks', ManageDecksContainer),
            },
            {
                path: 'settings',
                element: page('settings', SettingsContainer),
            },
            {
                path: 'verify-email',
                element: page('verify-email', VerifyEmail),
            },
            {
                path: 'user/id/*',
                element: page('user', UserDetails),
            },
            {
                path: 'user',
                element: page('LeaderBoard', LeaderBoard),
            },
            {
                path: '*',
                element: page('not-found', NotFound),
            },
        ],
    },
]);

export default function AppRouter() {
    return <RouterProvider router={router} fallbackElement={<ProgressCircle isIndeterminate size="L" />} />;
}
